// 指導支援 envelope の version 中立な共通 type. V2 / V3 のどちらでも変わらない
// dispatcher / status driver / fallback 構築層. caller (= GuidancePrepPane 等) は
// 本ファイルと version 別 dir (= v2 / v3) を組み合わせて画面を組む.

import type { GuidanceSupportDataV2 } from '../v2/types'
import type { GuidanceSupportDataV3 } from '../v3/types'

// 統合 envelope. discriminated union (`version`) で V2 / V3 を切り分ける.
export type GuidanceSupportData = GuidanceSupportDataV2 | GuidanceSupportDataV3

// envelope 全体の進行状態. 4 状態すべて HTTP 200 で返り、 status field で分岐する.
export type GuidanceStatus = 'processing' | 'completed' | 'failed' | 'skipped'

export interface ProcessingResponse {
  status: 'processing'
  estimatedSeconds: number
}

export interface CompletedResponse {
  status: 'completed'
  data: GuidanceSupportData
}

export interface FailedResponse {
  status: 'failed'
  error: {
    message: string
    code: string
  }
}

export interface SkippedResponse {
  status: 'skipped'
  reason: 'patient_not_linked'
  message: string
}

export type GuidanceApiResponse =
  | ProcessingResponse
  | CompletedResponse
  | FailedResponse
  | SkippedResponse

const hasStringStatus = (
  v: unknown,
  expected: GuidanceStatus
): v is { status: GuidanceStatus } =>
  typeof v === 'object' &&
  v !== null &&
  'status' in v &&
  (v as { status: unknown }).status === expected

export const isProcessingResponse = (v: unknown): v is ProcessingResponse =>
  hasStringStatus(v, 'processing')

export const isCompletedResponse = (v: unknown): v is CompletedResponse =>
  hasStringStatus(v, 'completed')

export const isFailedResponse = (v: unknown): v is FailedResponse =>
  hasStringStatus(v, 'failed')

export const isSkippedResponse = (v: unknown): v is SkippedResponse =>
  hasStringStatus(v, 'skipped')
